import { createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";
import { FilterMatchMode } from "@primevue/core/api";

export default createStore({
    state: {
        access_token: null,
        id_token: null,
        refresh_token: null,

        // RBAC capabilities for the current admin, fetched from
        // GET /admin/me/capabilities (issue #1878). Single source of truth for
        // menu/route visibility — never derived client-side from groups.
        capabilities: [],

        // Admin brand switcher (#2122). The brand the admin has selected. It
        // drives the X-Brand-Code header sent for management endpoints, and is
        // hydrated from GET /admin/me/brand so it stays in lock-step with the
        // backend-owned session brand used for player-data. playMode rides along
        // so play-mode-gated UI is correct from the first render. Persisted to
        // localStorage via createPersistedState; re-hydrated from the backend on
        // login so a stale/other-admin value can't linger.
        selectedBrand: { id: null, code: "giddyup", name: "GiddyUp", playMode: "real_money" },

        // dark theme
        darkTheme: false,

        // game category list filters
        gameCategoryListFilters: {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            group_id: { value: null, matchMode: FilterMatchMode.EQUALS },
            group_name: { value: null, matchMode: FilterMatchMode.CONTAINS }
        },
        gameCategoryListSort: {
            sortField: "group_id",
            sortOrder: 1
        },

        // game category detail filters
        gameCategoryDetailFilters: {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            game_id: { value: null, matchMode: FilterMatchMode.EQUALS },
            game_name: { value: null, matchMode: FilterMatchMode.CONTAINS }
        },
        gameCategoryDetailSort: {
            sortField: "game_id",
            sortOrder: 1
        },

        // game icon view filters
        gameIconViewFilters: {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS }
        },
        gameIconViewSort: {
            sortField: "name",
            sortOrder: 1
        },
        gameIconViewPagination: {
            currentPage: 1,
            itemsPerPage: 40
        },
        gameIconViewShowOnlyActive: false,

        // purchase queue caching
        purchaseQueueFilters: {
            // Text search fields
            user_email: "",
            user_display_name: "",
            product_name: "",
            merchant_transaction_id: "",
            product_id: null,
            brand_user_id: null,

            // Dropdown filters
            status: null,
            provider_code: null,

            // Date range
            start_date: null,
            end_date: null,

            // Amount range
            min_amount: null,
            max_amount: null
        },
        purchaseQueuePagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        purchaseQueueSort: {
            sortField: "created_at",
            sortOrder: "desc"
        },
        purchaseQueueAdvancedFiltersVisible: false,

        // redemption queue caching
        redemptionQueueFilters: {
            // Text search fields
            user_email: "",
            user_display_name: "",
            ref_id: "",
            brand_user_id: null,

            // Dropdown filters
            status: null,

            // Date range
            start_date: null,
            end_date: null,

            // Amount range
            min_amount: null,
            max_amount: null
        },
        redemptionQueuePagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        redemptionQueueSort: {
            sortField: "created_at",
            sortOrder: "desc"
        },
        redemptionQueueAdvancedFiltersVisible: false,

        // deposits queue caching
        depositsQueueFilters: {
            // Text search fields
            user_email: "",
            user_display_name: "",
            external_id: "",
            brand_user_id: null,

            // Dropdown filters
            status: null,
            provider_name: null,

            // Date range
            start_date: null,
            end_date: null,

            // Amount range
            min_amount: null,
            max_amount: null
        },
        depositsQueuePagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        depositsQueueSort: {
            sortField: "created_at",
            sortOrder: "desc"
        },
        depositsQueueAdvancedFiltersVisible: false,

        // withdrawals queue caching
        withdrawalsQueueFilters: {
            // Text search fields
            user_email: "",
            user_display_name: "",
            transaction_id: null,
            brand_user_id: null,
            assigned_to: "",

            // Dropdown filters
            status: null,
            provider_name: null,
            is_vip: null,

            // Date range
            start_date: null,
            end_date: null,

            // Amount range
            min_amount: null,
            max_amount: null
        },
        withdrawalsQueuePagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        withdrawalsQueueSort: {
            sortField: "request_date",
            sortOrder: "desc"
        },
        withdrawalsQueueAdvancedFiltersVisible: false,
        // Withdrawal-automation tab view (#1901): "all" | "manual_review" | "auto_pending" | "auto_approved_today"
        withdrawalsQueueActiveTab: "all",

        // payout review queue caching
        payoutReviewFilters: {
            status: "open",
            reason: "",
            provider_code: ""
        },
        payoutReviewPagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        payoutReviewSort: {
            sortField: "created_at",
            sortOrder: "desc"
        },

        // credit purchases queue caching
        creditPurchasesQueueFilters: {
            // Text search fields
            user_email: "",
            user_display_name: "",
            external_id: "",
            brand_user_id: null,

            // Dropdown filters
            status: null,
            product_id: null,

            // Date range
            start_date: null,
            end_date: null,

            // Amount range
            min_amount: null,
            max_amount: null
        },
        creditPurchasesQueuePagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        creditPurchasesQueueSort: {
            sortField: "created_at",
            sortOrder: "desc"
        },
        creditPurchasesQueueAdvancedFiltersVisible: false,

        // referral codes caching
        referralCodesFilters: {
            // Text search fields
            code: "",
            owner_email: "",
            brand_user_id: null,

            // Date range
            created_at_from: null,
            created_at_to: null,
            last_used_from: null,
            last_used_to: null,

            // Referral count filters
            min_referrals: null,
            max_referrals: null
        },
        referralCodesPagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        referralCodesSort: {
            sortField: "created_at",
            sortOrder: "desc"
        },
        referralCodesAdvancedFiltersVisible: false,

        // verification queue caching
        verificationQueueFilters: {
            // Text search fields
            user_email: "",
            user_display_name: "",
            brand_user_id: null,

            // Dropdown filters
            status: null,

            // Date range (Code Sent)
            code_sent_from: null,
            code_sent_to: null
        },
        verificationQueuePagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        verificationQueueSort: {
            sortField: "request_date",
            sortOrder: "desc"
        },
        verificationQueueAdvancedFiltersVisible: true,

        // verification queue tab state
        verificationQueueActiveTab: "0", // "0" for L2 Phone, "1" for L3 KYC

        // KYC queue caching
        kycQueueFilters: {
            // Text search fields
            user_email: "",
            user_display_name: "",
            brand_user_id: null,

            // Dropdown filters
            status: null,
            assigned_to: null,

            // Date range (Request to Socure)
            request_from: null,
            request_to: null
        },
        kycQueuePagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        kycQueueSort: {
            sortField: "request_date",
            sortOrder: "desc"
        },
        kycQueueAdvancedFiltersVisible: false,

        // AMOE queue caching
        amoeQueueFilters: {
            // Text search fields
            code: "",
            user_email: "",
            user_name: "",

            // Status filters
            status: null,
            submission_status: null,
            validity: null,

            // Date range
            generated_at_from: null,
            generated_at_to: null
        },
        amoeQueuePagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        amoeQueueSort: {
            sortField: "generated_at",
            sortOrder: "desc"
        },
        amoeQueueAdvancedFiltersVisible: false,

        // desync queue caching
        desyncQueueFilters: {
            // Search fields
            user_email: "",
            user_display_name: "",
            brand_user_id: null,

            // Duration filter
            min_stuck_minutes: null,

            // Amount range
            min_amount: null,
            max_amount: null
        },
        desyncQueuePagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        desyncQueueSort: {
            sortField: "created_at",
            sortOrder: "asc"
        },
        desyncQueueAdvancedFiltersVisible: false,

        // anti-fraud queue caching
        antiFraudQueueFilters: {
            status: null,
            match_type: null,
            confidence: null,
            match_id: null,
            user_email: ""
        },
        antiFraudQueuePagination: {
            first: 0,
            rows: 50,
            page: 1
        },
        antiFraudQueueSort: {
            sortField: "",
            sortOrder: "desc"
        },
        antiFraudQueueAdvancedFiltersVisible: false,

        // user list caching
        userListFilters: {
            // Text search fields
            email: "",
            brand_user_id: null,
            name: "",
            phone: "",
            display_name: "",
            external_id: "",

            // Dropdown filters
            verification_status: null,
            user_flag: null,

            // Date range
            start_date: null,
            end_date: null,

            // PII search depth
            pii_search_limit: 500
        },
        userListPagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        userListSort: {
            sortField: "registration_date",
            sortOrder: "desc"
        },
        userListAdvancedFiltersVisible: false,

        // recently visited links
        recentlyVisitedLinks: [],

        // game placement editor view preferences
        gamePlacementEditorPreferences: {
            useSmallIcons: false,
            minimizedCategories: []
        },

        // loyalty level filters
        loyaltyLevelFilters: {
            searchQuery: "",
            showInactiveTiers: true,
            sortBy: "display_order",
            sortOrder: "asc"
        },

        // affiliate queue caching
        affiliateQueueFilters: {
            // Text search fields
            email: "",
            name: "",

            // Dropdown filters
            status: null,
            hierarchy_level: null,

            // Date range
            created_at_from: null,
            created_at_to: null
        },
        affiliateQueuePagination: {
            first: 0,
            rows: 20,
            page: 1
        },
        affiliateQueueSort: {
            sortField: "created_at",
            sortOrder: "desc"
        },
        affiliateQueueAdvancedFiltersVisible: false,

    },
    getters: {
        access_token: state => state.access_token,
        id_token: state => state.id_token,
        refresh_token: state => state.refresh_token,

        // getter for dark theme
        isDarkTheme: state => state.darkTheme,

        // getter for game category list filters
        gameCategoryListFilters: state => state.gameCategoryListFilters,
        gameCategoryListSort: state => state.gameCategoryListSort,

        // getter for game category detail filters
        gameCategoryDetailFilters: state => state.gameCategoryDetailFilters,
        gameCategoryDetailSort: state => state.gameCategoryDetailSort,

        // getter for game icon view filters
        gameIconViewFilters: state => state.gameIconViewFilters,
        gameIconViewSort: state => state.gameIconViewSort,
        gameIconViewPagination: state => state.gameIconViewPagination,
        gameIconViewShowOnlyActive: state => state.gameIconViewShowOnlyActive,

        // getters for purchase queue caching
        purchaseQueueFilters: state => state.purchaseQueueFilters,
        purchaseQueuePagination: state => state.purchaseQueuePagination,
        purchaseQueueSort: state => state.purchaseQueueSort,
        purchaseQueueAdvancedFiltersVisible: state => state.purchaseQueueAdvancedFiltersVisible,

        // getters for redemption queue caching
        redemptionQueueFilters: state => state.redemptionQueueFilters,
        redemptionQueuePagination: state => state.redemptionQueuePagination,
        redemptionQueueSort: state => state.redemptionQueueSort,
        redemptionQueueAdvancedFiltersVisible: state => state.redemptionQueueAdvancedFiltersVisible,

        // getters for deposits queue caching
        depositsQueueFilters: state => state.depositsQueueFilters,
        depositsQueuePagination: state => state.depositsQueuePagination,
        depositsQueueSort: state => state.depositsQueueSort,
        depositsQueueAdvancedFiltersVisible: state => state.depositsQueueAdvancedFiltersVisible,

        // getters for withdrawals queue caching
        withdrawalsQueueFilters: state => state.withdrawalsQueueFilters,
        withdrawalsQueuePagination: state => state.withdrawalsQueuePagination,
        withdrawalsQueueSort: state => state.withdrawalsQueueSort,
        withdrawalsQueueAdvancedFiltersVisible: state => state.withdrawalsQueueAdvancedFiltersVisible,
        withdrawalsQueueActiveTab: state => state.withdrawalsQueueActiveTab,

        // getters for payout review queue caching
        payoutReviewFilters: state => state.payoutReviewFilters,
        payoutReviewPagination: state => state.payoutReviewPagination,
        payoutReviewSort: state => state.payoutReviewSort,

        // getters for credit purchases queue caching
        creditPurchasesQueueFilters: state => state.creditPurchasesQueueFilters,
        creditPurchasesQueuePagination: state => state.creditPurchasesQueuePagination,
        creditPurchasesQueueSort: state => state.creditPurchasesQueueSort,
        creditPurchasesQueueAdvancedFiltersVisible: state => state.creditPurchasesQueueAdvancedFiltersVisible,

        // getters for referral codes caching
        referralCodesFilters: state => state.referralCodesFilters,
        referralCodesPagination: state => state.referralCodesPagination,
        referralCodesSort: state => state.referralCodesSort,
        referralCodesAdvancedFiltersVisible: state => state.referralCodesAdvancedFiltersVisible,

        // getters for verification queue caching
        verificationQueueFilters: state => state.verificationQueueFilters,
        verificationQueuePagination: state => state.verificationQueuePagination,
        verificationQueueSort: state => state.verificationQueueSort,
        verificationQueueAdvancedFiltersVisible: state => state.verificationQueueAdvancedFiltersVisible,
        verificationQueueActiveTab: state => state.verificationQueueActiveTab,

        // getters for KYC queue caching
        kycQueueFilters: state => state.kycQueueFilters,
        kycQueuePagination: state => state.kycQueuePagination,
        kycQueueSort: state => state.kycQueueSort,
        kycQueueAdvancedFiltersVisible: state => state.kycQueueAdvancedFiltersVisible,

        // getters for AMOE queue caching
        amoeQueueFilters: state => state.amoeQueueFilters,
        amoeQueuePagination: state => state.amoeQueuePagination,
        amoeQueueSort: state => state.amoeQueueSort,
        amoeQueueAdvancedFiltersVisible: state => state.amoeQueueAdvancedFiltersVisible,

        // getters for desync queue caching
        desyncQueueFilters: state => state.desyncQueueFilters,
        desyncQueuePagination: state => state.desyncQueuePagination,
        desyncQueueSort: state => state.desyncQueueSort,
        desyncQueueAdvancedFiltersVisible: state => state.desyncQueueAdvancedFiltersVisible,

        // getters for anti-fraud queue caching
        antiFraudQueueFilters: state => state.antiFraudQueueFilters,
        antiFraudQueuePagination: state => state.antiFraudQueuePagination,
        antiFraudQueueSort: state => state.antiFraudQueueSort,
        antiFraudQueueAdvancedFiltersVisible: state => state.antiFraudQueueAdvancedFiltersVisible,

        // getters for user list caching
        userListFilters: state => state.userListFilters,
        userListPagination: state => state.userListPagination,
        userListSort: state => state.userListSort,
        userListAdvancedFiltersVisible: state => state.userListAdvancedFiltersVisible,

        // getter for recently visited links
        recentlyVisitedLinks: state => state.recentlyVisitedLinks,

        // getter for game placement editor view preferences
        gamePlacementEditorPreferences: state => state.gamePlacementEditorPreferences,
        gamePlacementUseSmallIcons: state => state.gamePlacementEditorPreferences.useSmallIcons,
        gamePlacementMinimizedCategories: state => state.gamePlacementEditorPreferences.minimizedCategories,

        // getters for loyalty level filters
        loyaltyLevelFilters: state => state.loyaltyLevelFilters,
        loyaltyLevelSearchQuery: state => state.loyaltyLevelFilters.searchQuery,
        loyaltyLevelShowInactiveTiers: state => state.loyaltyLevelFilters.showInactiveTiers,
        loyaltyLevelSortBy: state => state.loyaltyLevelFilters.sortBy,
        loyaltyLevelSortOrder: state => state.loyaltyLevelFilters.sortOrder,

        // getters for affiliate queue caching
        affiliateQueueFilters: state => state.affiliateQueueFilters,
        affiliateQueuePagination: state => state.affiliateQueuePagination,
        affiliateQueueSort: state => state.affiliateQueueSort,
        affiliateQueueAdvancedFiltersVisible: state => state.affiliateQueueAdvancedFiltersVisible,

        // Get filtered and sorted tiers
        getLoyaltyLevelFilteredTiers: state => tiers => {
            if (!tiers || !Array.isArray(tiers)) return [];

            let filteredTiers = [...tiers];

            // Filter by active status if toggle is off
            if (!state.loyaltyLevelFilters.showInactiveTiers) {
                filteredTiers = filteredTiers.filter(tier => tier.is_active);
            }

            // Filter by search query
            if (state.loyaltyLevelFilters.searchQuery.trim()) {
                const query = state.loyaltyLevelFilters.searchQuery.toLowerCase().trim();
                filteredTiers = filteredTiers.filter(
                    tier => tier.name?.toLowerCase().includes(query) || tier.description?.toLowerCase().includes(query)
                );
            }

            // Sort tiers
            filteredTiers.sort((a, b) => {
                let aValue = a[state.loyaltyLevelFilters.sortBy];
                let bValue = b[state.loyaltyLevelFilters.sortBy];

                // Handle numeric values
                if (
                    state.loyaltyLevelFilters.sortBy === "display_order" ||
                    state.loyaltyLevelFilters.sortBy === "min_points_to_achieve" ||
                    state.loyaltyLevelFilters.sortBy === "min_points_to_maintain"
                ) {
                    aValue = parseFloat(aValue) || 0;
                    bValue = parseFloat(bValue) || 0;
                }

                // Handle string values
                if (typeof aValue === "string") {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (state.loyaltyLevelFilters.sortOrder === "asc") {
                    return aValue > bValue ? 1 : -1;
                } else {
                    return aValue < bValue ? 1 : -1;
                }
            });

            return filteredTiers;
        }
    },
    mutations: {
        setAccessToken(state, access_token) {
            state.access_token = access_token;
        },
        setIdToken(state, id_token) {
            state.id_token = id_token;
        },
        setRefreshToken(state, refresh_token) {
            state.refresh_token = refresh_token;
        },
        clearTokens(state) {
            state.access_token = null;
            state.id_token = null;
            state.refresh_token = null;
            // Drop capabilities on logout so a different admin can't inherit the
            // previous session's (persisted) capability set.
            state.capabilities = [];
        },

        setCapabilities(state, capabilities) {
            state.capabilities = Array.isArray(capabilities) ? capabilities : [];
        },

        // Admin brand switcher (#2122). Stores the selected brand; the axios
        // interceptor reads state.selectedBrand.code for the X-Brand-Code header.
        // Accepts play_mode (backend shape) or playMode (frontend callers); a
        // payload with neither stores null (unknown) — never another brand's mode.
        setSelectedBrand(state, brand) {
            if (!brand || !brand.code) return;
            state.selectedBrand = {
                id: brand.id ?? null,
                code: brand.code,
                name: brand.name ?? brand.code,
                playMode: brand.play_mode ?? brand.playMode ?? null
            };
        },

        // mutation for dark theme
        toggleDarkTheme(state) {
            state.darkTheme = !state.darkTheme;
            document.documentElement.classList.toggle("app-dark");
        },
        setDarkTheme(state, isDark) {
            state.darkTheme = isDark;
            if (isDark) {
                document.documentElement.classList.add("app-dark");
            } else {
                document.documentElement.classList.remove("app-dark");
            }
        },

        // mutation to set game category list filters
        setGameCategoryListFilters(state, filters) {
            state.gameCategoryListFilters = filters;
        },
        setGameCategoryListSort(state, sort) {
            state.gameCategoryListSort = sort;
        },

        // mutation to set game category detail filters
        setGameCategoryDetailFilters(state, filters) {
            state.gameCategoryDetailFilters = filters;
        },
        setGameCategoryDetailSort(state, sort) {
            state.gameCategoryDetailSort = sort;
        },

        // mutation to set game icon view filters
        setGameIconViewFilters(state, filters) {
            state.gameIconViewFilters = filters;
        },
        setGameIconViewSort(state, sort) {
            state.gameIconViewSort = sort;
        },
        setGameIconViewPagination(state, pagination) {
            state.gameIconViewPagination = pagination;
        },
        setGameIconViewShowOnlyActive(state, showOnlyActive) {
            state.gameIconViewShowOnlyActive = showOnlyActive;
        },

        // mutations for purchase queue caching
        setPurchaseQueueFilters(state, filters) {
            state.purchaseQueueFilters = {
                ...state.purchaseQueueFilters,
                ...filters
            };
        },
        setPurchaseQueuePagination(state, pagination) {
            state.purchaseQueuePagination = {
                ...state.purchaseQueuePagination,
                ...pagination
            };
        },
        setPurchaseQueueSort(state, sort) {
            state.purchaseQueueSort = { ...state.purchaseQueueSort, ...sort };
        },
        setPurchaseQueueAdvancedFiltersVisible(state, visible) {
            state.purchaseQueueAdvancedFiltersVisible = visible;
        },
        clearPurchaseQueueFilters(state) {
            state.purchaseQueueFilters = {
                user_email: "",
                user_display_name: "",
                product_name: "",
                merchant_transaction_id: "",
                product_id: null,
                brand_user_id: null,
                status: null,
                provider_code: null,
                start_date: null,
                end_date: null,
                min_amount: null,
                max_amount: null
            };
        },

        // mutations for redemption queue caching
        setRedemptionQueueFilters(state, filters) {
            state.redemptionQueueFilters = {
                ...state.redemptionQueueFilters,
                ...filters
            };
        },
        setRedemptionQueuePagination(state, pagination) {
            state.redemptionQueuePagination = {
                ...state.redemptionQueuePagination,
                ...pagination
            };
        },
        setRedemptionQueueSort(state, sort) {
            state.redemptionQueueSort = { ...state.redemptionQueueSort, ...sort };
        },
        setRedemptionQueueAdvancedFiltersVisible(state, visible) {
            state.redemptionQueueAdvancedFiltersVisible = visible;
        },
        clearRedemptionQueueFilters(state) {
            state.redemptionQueueFilters = {
                user_email: "",
                user_display_name: "",
                ref_id: "",
                brand_user_id: null,
                status: null,
                start_date: null,
                end_date: null,
                min_amount: null,
                max_amount: null
            };
        },

        // mutations for deposits queue caching
        setDepositsQueueFilters(state, filters) {
            state.depositsQueueFilters = {
                ...state.depositsQueueFilters,
                ...filters
            };
        },
        setDepositsQueuePagination(state, pagination) {
            state.depositsQueuePagination = {
                ...state.depositsQueuePagination,
                ...pagination
            };
        },
        setDepositsQueueSort(state, sort) {
            state.depositsQueueSort = { ...state.depositsQueueSort, ...sort };
        },
        setDepositsQueueAdvancedFiltersVisible(state, visible) {
            state.depositsQueueAdvancedFiltersVisible = visible;
        },
        clearDepositsQueueFilters(state) {
            state.depositsQueueFilters = {
                user_email: "",
                user_display_name: "",
                external_id: "",
                brand_user_id: null,
                status: null,
                provider_name: null,
                start_date: null,
                end_date: null,
                min_amount: null,
                max_amount: null
            };
        },

        // mutations for withdrawals queue caching
        setWithdrawalsQueueFilters(state, filters) {
            state.withdrawalsQueueFilters = {
                ...state.withdrawalsQueueFilters,
                ...filters
            };
        },
        setWithdrawalsQueuePagination(state, pagination) {
            state.withdrawalsQueuePagination = {
                ...state.withdrawalsQueuePagination,
                ...pagination
            };
        },
        setWithdrawalsQueueSort(state, sort) {
            state.withdrawalsQueueSort = { ...state.withdrawalsQueueSort, ...sort };
        },
        setWithdrawalsQueueAdvancedFiltersVisible(state, visible) {
            state.withdrawalsQueueAdvancedFiltersVisible = visible;
        },
        setWithdrawalsQueueActiveTab(state, tab) {
            state.withdrawalsQueueActiveTab = tab || "all";
        },
        clearWithdrawalsQueueFilters(state) {
            state.withdrawalsQueueFilters = {
                user_email: "",
                user_display_name: "",
                transaction_id: null,
                brand_user_id: null,
                assigned_to: "",
                status: null,
                provider_name: null,
                is_vip: null,
                start_date: null,
                end_date: null,
                min_amount: null,
                max_amount: null
            };
            // The active tab is itself a filter (it pins auto_decision/status), so
            // "Clear Filters" must reset it too — otherwise the result set stays
            // scoped to e.g. auto_pending and the user can't tell why.
            state.withdrawalsQueueActiveTab = "all";
        },

        // mutations for payout review queue caching
        setPayoutReviewFilters(state, filters) {
            state.payoutReviewFilters = {
                ...state.payoutReviewFilters,
                ...filters
            };
        },
        setPayoutReviewPagination(state, pagination) {
            state.payoutReviewPagination = {
                ...state.payoutReviewPagination,
                ...pagination
            };
        },
        setPayoutReviewSort(state, sort) {
            state.payoutReviewSort = { ...state.payoutReviewSort, ...sort };
        },

        // mutations for credit purchases queue caching
        setCreditPurchasesQueueFilters(state, filters) {
            state.creditPurchasesQueueFilters = {
                ...state.creditPurchasesQueueFilters,
                ...filters
            };
        },
        setCreditPurchasesQueuePagination(state, pagination) {
            state.creditPurchasesQueuePagination = {
                ...state.creditPurchasesQueuePagination,
                ...pagination
            };
        },
        setCreditPurchasesQueueSort(state, sort) {
            state.creditPurchasesQueueSort = { ...state.creditPurchasesQueueSort, ...sort };
        },
        setCreditPurchasesQueueAdvancedFiltersVisible(state, visible) {
            state.creditPurchasesQueueAdvancedFiltersVisible = visible;
        },
        clearCreditPurchasesQueueFilters(state) {
            state.creditPurchasesQueueFilters = {
                user_email: "",
                user_display_name: "",
                external_id: "",
                brand_user_id: null,
                status: null,
                product_id: null,
                start_date: null,
                end_date: null,
                min_amount: null,
                max_amount: null
            };
        },

        // mutations for referral codes caching
        setReferralCodesFilters(state, filters) {
            state.referralCodesFilters = {
                ...state.referralCodesFilters,
                ...filters
            };
        },
        setReferralCodesPagination(state, pagination) {
            state.referralCodesPagination = {
                ...state.referralCodesPagination,
                ...pagination
            };
        },
        setReferralCodesSort(state, sort) {
            state.referralCodesSort = { ...state.referralCodesSort, ...sort };
        },
        setReferralCodesAdvancedFiltersVisible(state, visible) {
            state.referralCodesAdvancedFiltersVisible = visible;
        },
        clearReferralCodesFilters(state) {
            state.referralCodesFilters = {
                code: "",
                owner_email: "",
                brand_user_id: null,
                created_at_from: null,
                created_at_to: null,
                last_used_from: null,
                last_used_to: null,
                min_referrals: null,
                max_referrals: null
            };
        },

        // mutations for verification queue caching
        setVerificationQueueFilters(state, filters) {
            state.verificationQueueFilters = {
                ...state.verificationQueueFilters,
                ...filters
            };
        },
        setVerificationQueuePagination(state, pagination) {
            state.verificationQueuePagination = {
                ...state.verificationQueuePagination,
                ...pagination
            };
        },
        setVerificationQueueSort(state, sort) {
            state.verificationQueueSort = {
                ...state.verificationQueueSort,
                ...sort
            };
        },
        setVerificationQueueAdvancedFiltersVisible(state, visible) {
            state.verificationQueueAdvancedFiltersVisible = visible;
        },
        setVerificationQueueActiveTab(state, tab) {
            state.verificationQueueActiveTab = tab;
        },
        clearVerificationQueueFilters(state) {
            state.verificationQueueFilters = {
                user_email: "",
                user_display_name: "",
                brand_user_id: null,
                status: null,
                code_sent_from: null,
                code_sent_to: null
            };
        },

        // mutations for KYC queue caching
        setKycQueueFilters(state, filters) {
            state.kycQueueFilters = {
                ...state.kycQueueFilters,
                ...filters
            };
        },
        setKycQueuePagination(state, pagination) {
            state.kycQueuePagination = {
                ...state.kycQueuePagination,
                ...pagination
            };
        },
        setKycQueueSort(state, sort) {
            state.kycQueueSort = {
                ...state.kycQueueSort,
                ...sort
            };
        },
        setKycQueueAdvancedFiltersVisible(state, visible) {
            state.kycQueueAdvancedFiltersVisible = visible;
        },
        clearKycQueueFilters(state) {
            state.kycQueueFilters = {
                user_email: "",
                user_display_name: "",
                brand_user_id: null,
                status: null,
                assigned_to: null,
                request_from: null,
                request_to: null
            };
        },

        // mutations for AMOE queue caching
        setAMOEQueueFilters(state, filters) {
            state.amoeQueueFilters = {
                ...state.amoeQueueFilters,
                ...filters
            };
        },
        setAMOEQueuePagination(state, pagination) {
            state.amoeQueuePagination = {
                ...state.amoeQueuePagination,
                ...pagination
            };
        },
        setAMOEQueueSort(state, sort) {
            state.amoeQueueSort = { ...state.amoeQueueSort, ...sort };
        },
        setAMOEQueueAdvancedFiltersVisible(state, visible) {
            state.amoeQueueAdvancedFiltersVisible = visible;
        },
        clearAMOEQueueFilters(state) {
            state.amoeQueueFilters = {
                code: "",
                user_email: "",
                user_name: "",
                status: null,
                submission_status: null,
                validity: null,
                generated_at_from: null,
                generated_at_to: null
            };
        },

        // mutations for desync queue caching
        setDesyncQueueFilters(state, filters) {
            state.desyncQueueFilters = {
                ...state.desyncQueueFilters,
                ...filters
            };
        },
        setDesyncQueuePagination(state, pagination) {
            state.desyncQueuePagination = {
                ...state.desyncQueuePagination,
                ...pagination
            };
        },
        setDesyncQueueSort(state, sort) {
            state.desyncQueueSort = { ...state.desyncQueueSort, ...sort };
        },
        setDesyncQueueAdvancedFiltersVisible(state, visible) {
            state.desyncQueueAdvancedFiltersVisible = visible;
        },
        clearDesyncQueueFilters(state) {
            state.desyncQueueFilters = {
                user_email: "",
                user_display_name: "",
                brand_user_id: null,
                min_stuck_minutes: null,
                min_amount: null,
                max_amount: null
            };
        },

        // mutations for anti-fraud queue caching
        setAntiFraudQueueFilters(state, filters) {
            state.antiFraudQueueFilters = {
                ...state.antiFraudQueueFilters,
                ...filters
            };
        },
        setAntiFraudQueuePagination(state, pagination) {
            state.antiFraudQueuePagination = {
                ...state.antiFraudQueuePagination,
                ...pagination
            };
        },
        setAntiFraudQueueSort(state, sort) {
            state.antiFraudQueueSort = { ...state.antiFraudQueueSort, ...sort };
        },
        setAntiFraudQueueAdvancedFiltersVisible(state, visible) {
            state.antiFraudQueueAdvancedFiltersVisible = visible;
        },
        clearAntiFraudQueueFilters(state) {
            state.antiFraudQueueFilters = {
                status: null,
                match_type: null,
                confidence: null,
                match_id: null,
                user_email: ""
            };
        },

        // mutations for user list caching
        setUserListFilters(state, filters) {
            state.userListFilters = {
                ...state.userListFilters,
                ...filters
            };
        },
        setUserListPagination(state, pagination) {
            state.userListPagination = {
                ...state.userListPagination,
                ...pagination
            };
        },
        setUserListSort(state, sort) {
            state.userListSort = { ...state.userListSort, ...sort };
        },
        setUserListAdvancedFiltersVisible(state, visible) {
            state.userListAdvancedFiltersVisible = visible;
        },
        clearUserListFilters(state) {
            state.userListFilters = {
                // Text search fields
                email: "",
                brand_user_id: null,
                name: "",
                phone: "",
                display_name: "",
                external_id: "",

                // Dropdown filters
                verification_status: null,
                user_flag: null,

                // Date range
                start_date: null,
                end_date: null,

                // PII search depth
                pii_search_limit: 500
            };
        },

        // mutations for recently visited links
        addRecentlyVisitedLink(state, link) {
            // Remove if already exists to avoid duplicates
            const existingIndex = state.recentlyVisitedLinks.findIndex(item => item.path === link.path);
            if (existingIndex > -1) {
                state.recentlyVisitedLinks.splice(existingIndex, 1);
            }

            // Add to beginning of array
            state.recentlyVisitedLinks.unshift(link);

            // Keep only last 4 items
            if (state.recentlyVisitedLinks.length > 4) {
                state.recentlyVisitedLinks = state.recentlyVisitedLinks.slice(0, 4);
            }
        },
        clearRecentlyVisitedLinks(state) {
            state.recentlyVisitedLinks = [];
        },

        // mutations for game placement editor view preferences
        setGamePlacementEditorPreferences(state, preferences) {
            state.gamePlacementEditorPreferences = {
                ...state.gamePlacementEditorPreferences,
                ...preferences
            };
        },
        setGamePlacementUseSmallIcons(state, useSmallIcons) {
            state.gamePlacementEditorPreferences.useSmallIcons = useSmallIcons;
        },
        setGamePlacementMinimizedCategories(state, minimizedCategories) {
            state.gamePlacementEditorPreferences.minimizedCategories = minimizedCategories;
        },
        addGamePlacementMinimizedCategory(state, categoryId) {
            if (!state.gamePlacementEditorPreferences.minimizedCategories.includes(categoryId)) {
                state.gamePlacementEditorPreferences.minimizedCategories.push(categoryId);
            }
        },
        removeGamePlacementMinimizedCategory(state, categoryId) {
            const index = state.gamePlacementEditorPreferences.minimizedCategories.indexOf(categoryId);
            if (index > -1) {
                state.gamePlacementEditorPreferences.minimizedCategories.splice(index, 1);
            }
        },
        clearGamePlacementMinimizedCategories(state) {
            state.gamePlacementEditorPreferences.minimizedCategories = [];
        },

        // loyalty level filters
        setLoyaltyLevelSearchQuery(state, query) {
            state.loyaltyLevelFilters.searchQuery = query;
        },
        setLoyaltyLevelShowInactiveTiers(state, show) {
            state.loyaltyLevelFilters.showInactiveTiers = show;
        },
        setLoyaltyLevelSortBy(state, field) {
            state.loyaltyLevelFilters.sortBy = field;
        },
        setLoyaltyLevelSortOrder(state, order) {
            state.loyaltyLevelFilters.sortOrder = order;
        },
        toggleLoyaltyLevelSortOrder(state) {
            state.loyaltyLevelFilters.sortOrder = state.loyaltyLevelFilters.sortOrder === "asc" ? "desc" : "asc";
        },
        clearLoyaltyLevelFilters(state) {
            state.loyaltyLevelFilters.searchQuery = "";
            state.loyaltyLevelFilters.showInactiveTiers = true;
            state.loyaltyLevelFilters.sortBy = "display_order";
            state.loyaltyLevelFilters.sortOrder = "asc";
        },

        // mutations for affiliate queue caching
        setAffiliateQueueFilters(state, filters) {
            state.affiliateQueueFilters = {
                ...state.affiliateQueueFilters,
                ...filters
            };
        },
        setAffiliateQueuePagination(state, pagination) {
            state.affiliateQueuePagination = {
                ...state.affiliateQueuePagination,
                ...pagination
            };
        },
        setAffiliateQueueSort(state, sort) {
            state.affiliateQueueSort = { ...state.affiliateQueueSort, ...sort };
        },
        setAffiliateQueueAdvancedFiltersVisible(state, visible) {
            state.affiliateQueueAdvancedFiltersVisible = visible;
        },
        clearAffiliateQueueFilters(state) {
            state.affiliateQueueFilters = {
                email: "",
                name: "",
                status: null,
                hierarchy_level: null,
                created_at_from: null,
                created_at_to: null
            };
        },

    },
    actions: {},
    modules: {},
    plugins: [createPersistedState()]
});
